// ============================================================================
// L'ÉCHELLE DES PLANS — qui passe au-dessus de qui, décidé UNE fois.
//
// ⚠️ POURQUOI CE FICHIER EXISTE : sans échelle nommée, chaque app choisit son nombre, et l'ordre des
// plans devient un accident d'écriture. Deux cas RÉELS l'ont montré avant même l'extraction — une
// modale de travail posée au-dessus du plan des pickers (donc un picker ouvert DANS la modale passait
// dessous, c'est-à-dire nulle part), et un tiroir à 600 en collision avec le panneau de colonnes. Un
// popover à 600 passerait au-dessus d'une modale à 100 : au-dessus de son propre parent.
//
// ⚠️ LES DEUX ANCRES SONT CELLES DE LA PRODUCTION, et elles ne bougent pas : le dialogue bloquant vit
// à 1100 et le toast à 1200 depuis toujours. L'échelle se cale dessous plutôt que de renuméroter ce
// qui marche — une renumérotation générale ferait bouger des écrans validés sans qu'aucun test ne
// puisse l'attraper.
//
// LES DEUX RÈGLES D'ORDRE, dont tout le reste découle :
//   1. POPOVER > MODALE DE TRAVAIL — un picker, un menu, une liste de suggestions vivent DANS une
//      modale. L'inverse rend le contrôle inatteignable.
//   2. BLOQUANT > POPOVER — une confirmation ou une alerte interrompt tout, y compris un menu ouvert.
//      Sinon on confirme une action qu'un panneau recouvre.
//
// Chaque rôle porte son plan de VOILE juste en dessous du sien : un voile et son panneau se déplacent
// ensemble, et les séparer les fait glisser l'un par rapport à l'autre à la première insertion.
// ============================================================================

/**
 * Les plans, du plus profond au plus haut.
 * ⚠️ AUCUN NOMBRE EN DUR AILLEURS : un `zIndex: 60` écrit dans une app est un ordre déclaré hors de
 * cette échelle, donc invérifiable — c'est exactement ce qui a produit les deux collisions ci-dessus.
 */
export const Z_LAYERS = {
  /** En-tête de tableau collant, barres internes. Reste SOUS tout ce qui se superpose à la page. */
  stickyHeader: 100,
  /**
   * Infobulle qui SUIT LE CURSEUR (graphique). Au-dessus de la page, SOUS tout ce qui se superpose.
   * ⚠️ La maquette la pose à 900 — le plan de la modale. On ne la suit pas ici : une bulle de survol
   * qui passerait au-dessus d'un tiroir ou d'un dialogue serait un artefact flottant sur un panneau
   * qu'elle ne concerne pas. Sous 690, elle reste couverte par tout ce qui ouvre.
   */
  chartTip: 680,
  /** Rail mobile et tiroirs latéraux. */
  drawerScrim: 690,
  drawer: 700,
  /** Modale de TRAVAIL (formulaire, fiche) — celle qu'on peut fermer pour aller lire l'écran. */
  modalScrim: 890,
  modal: 900,
  /** Popover, menu, picker, suggestions. ⚠️ AU-DESSUS de la modale : ils vivent dedans. */
  popoverScrim: 990,
  popover: 1000,
  /** Fantôme de glisser-déposer : au-dessus des panneaux qu'il survole, sous ce qui bloque. */
  dragGhost: 1050,
  /** Dialogue BLOQUANT (confirmation, alerte). Ancre de production — ne pas déplacer. */
  blockingScrim: 1090,
  blocking: 1100,
  /**
   * Infobulle ANCRÉE à un élément (explication d'un contrôle, raison d'un verrou).
   * ⚠️ AU-DESSUS DE TOUT CE QUI S'OUVRE, contrairement à `chartTip` — et la différence est de nature,
   * pas de degré. La bulle du graphique SUIT LE CURSEUR : elle peut donc dériver au-dessus d'un panneau
   * qu'elle ne concerne pas, d'où son plan bas. Celle-ci est POSÉE SUR SON DÉCLENCHEUR : si ce
   * déclencheur est couvert, il n'est pas survolable, donc la bulle n'existe pas — elle ne peut pas
   * devenir un artefact orphelin. Et comme elle est transparente au pointeur, elle ne vole aucun clic.
   * ⚠️ SOUS LE TOAST : un message système reste au-dessus de tout, y compris d'une explication.
   */
  tooltip: 1150,
  /** Toast. Ancre de production — ne pas déplacer. Rien ne passe au-dessus d'un message système. */
  toast: 1200,
} as const;

